/*
 * Whether the draft card's Approve bar may hold the thread pane's bottom edge (step 10, round 3).
 * Only once the rep has scrolled into the draft: its body's top must sit above the bar with its first
 * lines showing, so Approve never shows over draft text not yet on screen. Until then the bar keeps
 * its place under the draft, and a conversation that opens with the draft below the fold opens with
 * Approve there too. `agent-item.css` holds the bar while `data-held` is set.
 */
import { type RefObject, useEffect, useState } from "react";

/** The pane the thread scrolls in; a phone scrolls the whole conversation and holds nothing. */
const SCROLLER = ".lcr-i2-thread";
/** The draft's text on the card (`ReadOnlyDraft` in `work-pane.tsx`). */
const DRAFT = ".lcr-approved-draft";
/** The draft body's top padding and about three of its lines, which show above the held bar. */
const FIRST_LINES = 88;

/**
 * `shown` changes whenever the card swaps the draft's text for another view of it (the reply box
 * holding it open), so the body in view is the one watched.
 */
export function useHeldAtFoot(bar: RefObject<HTMLElement | null>, shown: string): boolean {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    const node = bar.current;
    const draft = node?.closest("article")?.querySelector(DRAFT);
    const scroller = node?.closest(SCROLLER);
    setHeld(false);
    if (!shown || !node || !draft || !scroller || typeof IntersectionObserver === "undefined")
      return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeld(entry?.isIntersecting === true),
      {
        root: scroller,
        // The pane above the bar and the draft's first lines: the body's top inside it is in view.
        rootMargin: `0px 0px -${node.offsetHeight + FIRST_LINES}px 0px`,
        threshold: 0,
      },
    );
    observer.observe(draft);
    return () => observer.disconnect();
  }, [bar, shown]);
  return held;
}
